/**
 * @fileoverview Shared sign-in form component for shell package
 *
 * @purpose Reusable sign-in form component that handles user authentication using Better Auth's
 *          native signIn.email() function. Supports rememberMe functionality and uses window.location.href
 *          for navigation after successful login to ensure cookies are sent with the request.
 *          When callbackUrl, paramError, showRegisteredSuccess are passed (from SignInPage server component),
 *          SignInForm renders SignInFormContent directly and never calls useSearchParams(), avoiding
 *          useContext null during SSR in standalone/Docker.
 *
 * @usage Import and use in shell layouts or derivative apps:
 *        `<SignInForm config={shellConfig} defaultCallbackUrl="/dashboard" />`
 *        Or from SignInPage (server) with callbackUrl, paramError, showRegisteredSuccess so no useSearchParams on server.
 *
 * @ownership Shell package - authentication UI components
 *
 * @dependencies
 * - SignInFormContent.tsx (form UI; receives callbackUrl and error from props)
 * - @/lib/better-auth/client (signIn hook)
 *
 * @see SignInFormContent.tsx (form implementation)
 */
'use client';

import type { SignInFormProps } from './SignInForm.types';
import { SignInFormContent } from './SignInFormContent';
import { SignInFormFromClientParams } from './SignInFormFromClientParams';

// Re-export for callers that import SignInFormProps from SignInForm
export type { SignInFormProps } from './SignInForm.types';

export function SignInForm(props: SignInFormProps) {
  // Prefer server-passed params when present (from SignInPage) to avoid useSearchParams during SSR.
  const hasServerParams =
    props.callbackUrl !== undefined &&
    props.paramError !== undefined &&
    props.showRegisteredSuccess !== undefined;

  if (hasServerParams) {
    return (
      <SignInFormContent
        config={props.config}
        defaultCallbackUrl={props.defaultCallbackUrl}
        className={props.className}
        callbackUrl={props.callbackUrl!}
        paramError={props.paramError!}
        showRegisteredSuccess={props.showRegisteredSuccess!}
      />
    );
  }

  // Server (standalone/Docker): never run the component that calls useSearchParams (no context).
  if (typeof window === 'undefined') {
        const defaultCallback = props.defaultCallbackUrl ?? props.config?.routes?.homepage ?? '/home';
    return (
      <SignInFormContent
        config={props.config}
        defaultCallbackUrl={props.defaultCallbackUrl}
        className={props.className}
        callbackUrl={defaultCallback}
        paramError=""
        showRegisteredSuccess={false}
      />
    );
  }

  return <SignInFormFromClientParams {...props} />;
}
